import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useT } from "../i18n";
import {
  createFromIdea,
  deleteFile,
  duplicateFile,
  exportFileUrl,
  fetchGenerators,
  fetchMaterials,
  fetchPrinters,
  generateFile,
  getProject,
  listFarmPrinters,
  printOrder,
  projectExportUrl,
  projectNoticeUrl,
  projectPreviewUrl,
  type FarmPrinter,
  type PrintOrderItem,
  renameFile,
  routeFile,
  shareProject,
  sliceFile,
  thumbnailUrl,
  type GeneratorInfo,
  type Project,
  type ProjectFile,
} from "../api";
import { Icon } from "../components/Icon";
import { PublishPanel } from "../components/PublishPanel";
import { Empty, Modal, StatusBadge } from "../components/ui";
import { useToast } from "../lib/toast";
import type { MaterialProfile, PrinterProfile } from "../types";
import { PartThumb } from "../scene/PartThumb";

export function ProjectDetailPage() {
  const tr = useT();
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [order, setOrder] = useState<PrintOrderItem[]>([]);
  const [previewOk, setPreviewOk] = useState(true);
  const [gens, setGens] = useState<GeneratorInfo[]>([]);
  const [printers, setPrinters] = useState<PrinterProfile[]>([]);
  const [materials, setMaterials] = useState<MaterialProfile[]>([]);
  const [tab, setTab] = useState<"generate" | "idea">("generate");
  const [gen, setGen] = useState("bracket");
  const [idea, setIdea] = useState("");
  const [mode, setMode] = useState<"parametric" | "generative">("parametric");
  const [printer, setPrinter] = useState("prusa_mk4_0.4");
  const [material, setMaterial] = useState("PLA");
  const [busy, setBusy] = useState(false);
  // Route dialog: batch quantity + optional scheduled start, to a farm printer.
  const [farm, setFarm] = useState<FarmPrinter[]>([]);
  const [routeFor, setRouteFor] = useState<ProjectFile | null>(null);
  const [rPrinter, setRPrinter] = useState("");
  const [rQty, setRQty] = useState(1);
  const [rSched, setRSched] = useState("");
  const toast = useToast((s) => s.show);

  // Materials compatible with the selected printer (drives the material dropdown).
  const compatMats = (() => {
    const p = printers.find((x) => x.id === printer);
    const ids = p?.compatible_materials ?? materials.map((m) => m.id);
    return materials.filter((m) => ids.includes(m.id));
  })();
  const printerName = (id: string) => printers.find((p) => p.id === id)?.name ?? id;

  const load = useCallback(async () => {
    try {
      const p = await getProject(id);
      setProject(p);
      setFiles(p.files ?? []);
      printOrder(id).then(setOrder).catch(() => setOrder([]));
    } catch (e) {
      toast(String((e as Error).message), "bad");
    }
  }, [id, toast]);

  useEffect(() => {
    load();
    fetchGenerators().then(setGens).catch(() => {});
    fetchPrinters().then((ps) => {
      setPrinters(ps);
      if (ps.length && !ps.some((p) => p.id === printer)) setPrinter(ps[0].id);
    }).catch(() => {});
    fetchMaterials().then(setMaterials).catch(() => {});
    listFarmPrinters().then(setFarm).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  function openRoute(f: ProjectFile) {
    setRouteFor(f);
    setRPrinter(farm[0]?.name ?? "farm-1");
    setRQty(1);
    setRSched("");
  }
  async function doRoute() {
    if (!routeFor) return;
    try {
      const r = await routeFile(id, routeFor.id, rPrinter, { quantity: rQty, scheduled_at: rSched ? new Date(rSched).toISOString() : null });
      setRouteFor(null);
      toast(rSched ? `Planifié (${r.quantity}×)` : r.quantity > 1 ? `${r.quantity} copies en file` : "Routé vers l'imprimante");
      load();
    } catch (e) {
      toast(String((e as Error).message), "bad");
    }
  }

  async function doGenerate() {
    setBusy(true);
    try {
      await generateFile(id, { generator: gen, params: {}, printer_profile: printer, material });
      toast("Fichier généré + validé par l'INSPECTOR");
      load();
    } catch (e) {
      toast(String((e as Error).message), "bad");
    } finally {
      setBusy(false);
    }
  }
  async function doIdea() {
    setBusy(true);
    try {
      const f = await createFromIdea(id, { idea, mode, printer_profile: printer, material });
      toast(f.passed ? "Conçu et prêt à imprimer" : "Brouillon généré (à revoir)", f.passed ? "ok" : "bad");
      setIdea("");
      load();
    } catch (e) {
      toast(String((e as Error).message), "bad");
    } finally {
      setBusy(false);
    }
  }
  async function fileAction(fn: () => Promise<unknown>, ok: string, kind: "ok" | "bad" = "ok") {
    try { await fn(); toast(ok, kind); load(); } catch (e) { toast(String((e as Error).message), "bad"); }
  }

  if (!project) return <div className="muted">Chargement…</div>;
  const okCount = files.filter((f) => f.passed).length;

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            <a style={{ cursor: "pointer", color: "var(--muted)" }} href="/projects" onClick={(e) => { e.preventDefault(); nav("/projects"); }}>PROJETS</a>
            <span style={{ margin: "0 7px", color: "var(--muted-2)" }}>/</span>
            <span style={{ color: "var(--text-2)" }}>{project.name}</span>
          </div>
          <h1>{project.name}</h1>
          <div className="sub">{project.description || "—"}</div>
        </div>
        <div className="row">
          {project.shared && <span className="badge accent"><span className="dot" />{tr("partagé")}</span>}
          <button
            className="btn"
            onClick={() => fileAction(async () => {
              const r = await shareProject(id);
              await navigator.clipboard?.writeText(`${location.origin}/shared/${r.share_token}`).catch(() => {});
            }, "Lien de partage copié")}
          >
            <Icon name="share" size={15} /> Partager
          </button>
        </div>
      </div>

      {/* Project overview: all parts visualised in suggested print order + key info. */}
      {files.length > 0 && (
        <div className="card" style={{ marginBottom: 22 }}>
          <div className="row between" style={{ marginBottom: 14 }}>
            <div className="row" style={{ gap: 9 }}><Icon name="layers" size={16} /><h2>{tr("Aperçu du projet")}</h2></div>
            <span className="eyebrow">{tr("rendu global · toutes les pièces")}</span>
          </div>
          {/* Whole-project render: all parts laid out together. */}
          {previewOk && (
            <div style={{ background: "radial-gradient(700px 400px at 50% 35%, #0e1828, #070b12 78%)", borderRadius: "var(--r-sm)", border: "1px solid var(--line)", marginBottom: 14, display: "grid", placeItems: "center", minHeight: 220, overflow: "hidden" }}>
              <img src={projectPreviewUrl(id)} alt="Rendu global du projet" loading="lazy" decoding="async" onError={() => setPreviewOk(false)} style={{ width: "100%", maxHeight: 360, objectFit: "contain" }} />
            </div>
          )}
          <div className="eyebrow" style={{ marginBottom: 8 }}>{order.length ? "Pièces · ordre d'impression conseillé" : "Pièces"}</div>
          <div className="row" style={{ gap: 12, overflowX: "auto", paddingBottom: 6 }}>
            {(order.length ? order.map((o) => files.find((f) => f.id === o.id)).filter(Boolean) as ProjectFile[] : files).map((f, i) => (
              <div key={f.id} style={{ flexShrink: 0, width: 116, cursor: "pointer" }} onClick={() => nav(`/studio?project=${id}&file=${f.id}`)} title={tr("Ouvrir dans le Studio 3D")}>
                <div style={{ position: "relative" }}>
                  <PartThumb material={f.material} passed={f.passed} size={116} src={f.has_thumb ? thumbnailUrl(id, f.id) : undefined} />
                  <span className="mono" style={{ position: "absolute", top: 5, left: 6, fontSize: 11, color: "var(--accent-3)", background: "#0009", borderRadius: 5, padding: "1px 5px" }}>{i + 1}</span>
                </div>
                <div style={{ fontSize: 11.5, marginTop: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--text-2)" }}>{f.name}</div>
              </div>
            ))}
          </div>
          <div className="row wrap" style={{ gap: 22, marginTop: 14, paddingTop: 13, borderTop: "1px solid var(--line)" }}>
            <div><div className="eyebrow">{tr("Pièces")}</div><div className="num" style={{ fontSize: 17, marginTop: 2 }}>{files.length}</div></div>
            <div><div className="eyebrow">{tr("Prêtes")}</div><div className="num" style={{ fontSize: 17, marginTop: 2, color: okCount === files.length ? "var(--ok)" : "var(--warn)" }}>{okCount}/{files.length}</div></div>
            <div><div className="eyebrow">{tr("Matières")}</div><div style={{ fontSize: 14, marginTop: 4 }}>{[...new Set(files.map((f) => f.material))].join(" · ")}</div></div>
            <div><div className="eyebrow">Machine</div><div style={{ fontSize: 14, marginTop: 4 }}>{printerName(files[0]?.printer_profile ?? "")}</div></div>
          </div>
        </div>
      )}

      {/* Printable build notice */}
      {files.length > 0 && (
        <div className="card" style={{ marginBottom: 22 }}>
          <div className="row between wrap" style={{ gap: 12 }}>
            <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center", color: "var(--accent-3)", background: "var(--accent-soft)", border: "1px solid var(--accent-line)" }}><Icon name="external" size={18} /></div>
              <div>
                <div className="card-title">Notice d'impression & assemblage</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 3, maxWidth: 560, lineHeight: 1.5 }}>
                  PDF complet pour la construction live : réglages par matière (couche, parois, remplissage, températures), ordre d'impression + temps/filament par pièce, visserie & quincaillerie déduites, et étapes d'assemblage détaillées.
                </div>
              </div>
            </div>
            <div className="row wrap" style={{ gap: 8 }}>
              <a className="btn primary" href={projectNoticeUrl(id)} target="_blank" rel="noreferrer"><Icon name="external" size={15} /> Notice PDF</a>
              <a className="btn" href={projectExportUrl(id)} download><Icon name="download" size={15} /> Projet (ZIP: STL + render + fiche)</a>
            </div>
          </div>
        </div>
      )}

      {/* Marketplace publishing */}
      {files.length > 0 && <PublishPanel projectId={id} />}

      {/* Création */}
      <div className="card" style={{ marginBottom: 22 }}>
        <div className="row between" style={{ marginBottom: 16 }}>
          <div className="tabs">
            <button className={tab === "generate" ? "on" : ""} onClick={() => setTab("generate")}>{tr("Générateur")}</button>
            <button className={tab === "idea" ? "on" : ""} onClick={() => setTab("idea")}>{tr("Depuis une idée · IA")}</button>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <select
              value={printer}
              onChange={(e) => {
                const np = e.target.value;
                setPrinter(np);
                const p = printers.find((x) => x.id === np);
                const ids = p?.compatible_materials ?? [];
                if (ids.length && !ids.includes(material)) setMaterial(ids[0]);
              }}
              style={{ fontSize: 12.5 }}
            >
              {printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={material} onChange={(e) => setMaterial(e.target.value)} style={{ fontSize: 12.5 }}>
              {compatMats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>

        {tab === "generate" ? (
          <div className="row wrap" style={{ alignItems: "flex-end", gap: 12 }}>
            <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 220 }}>
              <label>{tr("Générateur paramétrique")}</label>
              <select value={gen} onChange={(e) => setGen(e.target.value)}>
                {gens.map((g) => <option key={g.name} value={g.name}>{g.title}</option>)}
              </select>
            </div>
            <button className="btn primary" disabled={busy} onClick={doGenerate}>
              {busy ? <><span className="spinner" /> {tr("Génération…")}</> : <><Icon name="zap" size={15} /> {tr("Générer + valider")}</>}
            </button>
            <span className="muted row" style={{ gap: 6, fontSize: 12, flexBasis: "100%" }}>
              <Icon name="shield" size={12} /> Le maillage passe par l'INSPECTOR (fail-closed) avant d'être ajouté au projet.
            </span>
          </div>
        ) : (
          <div className="col" style={{ gap: 12 }}>
            <div className="row wrap" style={{ gap: 8 }}>
              <div className="tabs">
                <button className={mode === "parametric" ? "on" : ""} onClick={() => setMode("parametric")}>{tr("CAD paramétrique (LLM)")}</button>
                <button className={mode === "generative" ? "on" : ""} onClick={() => setMode("generative")}>{tr("Maillage génératif (fal/kai)")}</button>
              </div>
            </div>
            <textarea value={idea} onChange={(e) => setIdea(e.target.value)}
              placeholder={mode === "parametric"
                ? "Décris ton objet : « un support de téléphone incliné à 60° avec une butée avant et deux trous M3 »"
                : "Décris l'objet à générer en 3D : « une figurine de dragon stylisée, base ronde »"} />
            <div className="row wrap">
              <button className="btn primary" disabled={busy || !idea} onClick={doIdea}>
                {busy ? <><span className="spinner" /> Conception…</> : <><Icon name="sparkle" size={15} /> {mode === "parametric" ? "Concevoir (LLM → CAD → gate)" : "Générer (IA → maillage → gate)"}</>}
              </button>
              <span className="muted" style={{ fontSize: 12 }}>
                {mode === "parametric"
                  ? "Le LLM écrit du CAD paramétrique, routé via CreditsForge / ComputeForge / Ollama, exécuté puis validé."
                  : "Génération via fal.ai / kai.ai (ou GPU local TRELLIS/Hunyuan3D). Le maillage reste un brouillon jusqu'au gate INSPECTOR."}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Fichiers */}
      <div className="row between" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 10 }}>
          <h2>Fichiers</h2>
          <span className="badge"><span className="num">{files.length}</span></span>
        </div>
        {files.length > 0 && <span className="badge ok"><span className="dot" /><span className="num">{okCount}/{files.length}</span> {tr("prêts à imprimer")}</span>}
      </div>

      {files.length === 0 ? (
        <Empty icon="box">{tr("Aucun fichier. Génère un objet ou décris une idée ci-dessus.")}</Empty>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr><th>Nom</th><th>Source</th><th>{tr("Machine · matière")}</th><th>Score</th><th>Statut</th><th style={{ textAlign: "right" }}>Actions</th></tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id}>
                  <td>
                    <div className="row" style={{ gap: 11 }}>
                      <PartThumb material={f.material} passed={f.passed} size={42} src={f.has_thumb ? thumbnailUrl(id, f.id) : undefined} />
                      <a style={{ fontWeight: 600, cursor: "pointer" }} title={tr("Ouvrir dans le Studio 3D")} onClick={() => nav(`/studio?project=${id}&file=${f.id}`)}>{f.name}</a>
                    </div>
                  </td>
                  <td><span className="badge">{f.generator || f.kind}</span></td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {printerName(f.printer_profile)}
                    <span style={{ color: "var(--muted-2)" }}> · </span>{f.material}
                  </td>
                  <td><span className="num" style={{ color: f.score >= 0.85 ? "var(--ok)" : f.score >= 0.7 ? "var(--warn)" : "var(--bad)" }}>{f.score.toFixed(2)}</span></td>
                  <td><StatusBadge passed={f.passed} /></td>
                  <td>
                    <div className="row" style={{ justifyContent: "flex-end" }}>
                      <button className="btn sm ghost" title={tr("Ouvrir dans le Studio 3D")} onClick={() => nav(`/studio?project=${id}&file=${f.id}`)}>
                        <Icon name="studio" size={14} /> Studio
                      </button>
                      {f.passed && (
                        <button
                          className="btn sm ghost"
                          title={tr("Slice / estimer (temps · filament · coût)")}
                          onClick={async () => {
                            try {
                              const e = await sliceFile(id, f.id);
                              const m = Math.round(e.print_time_s / 60);
                              toast(`${e.authoritative ? "Tranché" : "Estimé"} · ${m} min · ${e.filament_g.toFixed(0)} g · ${(e.quote.material_cost ?? 0).toFixed(2)} €`);
                            } catch (err) { toast(String((err as Error).message), "bad"); }
                          }}
                        >
                          <Icon name="generators" size={14} /> Estimer
                        </button>
                      )}
                      {f.passed && (
                        <button className="btn sm" title="Router / planifier vers une imprimante" onClick={() => openRoute(f)}>
                          <Icon name="wifi" size={14} /> Imprimer
                        </button>
                      )}
                      <button className="btn sm ghost icon" title="Renommer" onClick={() => { const n = prompt("Renommer le fichier", f.name); if (n) fileAction(() => renameFile(id, f.id, n), "Renommé"); }}><Icon name="edit" size={14} /></button>
                      <button className="btn sm ghost icon" title="Dupliquer" onClick={() => fileAction(() => duplicateFile(id, f.id), "Dupliqué")}><Icon name="copy" size={14} /></button>
                      <a className="btn sm ghost" href={exportFileUrl(id, f.id, "stl")} target="_blank" rel="noreferrer">STL</a>
                      <a className="btn sm ghost" href={exportFileUrl(id, f.id, "json")} target="_blank" rel="noreferrer">JSON</a>
                      <button className="btn sm danger icon" title="Supprimer" onClick={() => fileAction(() => deleteFile(id, f.id), "Supprimé", "bad")}><Icon name="trash" size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {routeFor && (
        <Modal
          title={`Imprimer · ${routeFor.name}`}
          onClose={() => setRouteFor(null)}
          footer={<><button className="btn ghost" onClick={() => setRouteFor(null)}>Annuler</button><button className="btn primary" disabled={!rPrinter} onClick={doRoute}><Icon name="wifi" size={14} /> {rSched ? "Planifier" : "Mettre en file"}</button></>}
        >
          <div className="field">
            <label>Imprimante (ferme)</label>
            <input list="pf-farm" value={rPrinter} onChange={(e) => setRPrinter(e.target.value)} placeholder={tr("Nom de l'imprimante")} autoFocus />
            <datalist id="pf-farm">{farm.map((p) => <option key={p.id} value={p.name} />)}</datalist>
          </div>
          <div className="row" style={{ gap: 12 }}>
            <div className="field" style={{ flex: 1 }}><label>{tr("Quantité (copies)")}</label><input type="number" min={1} max={50} value={rQty} onChange={(e) => setRQty(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} /></div>
            <div className="field" style={{ flex: 1.4 }}><label>Planifier (optionnel)</label><input type="datetime-local" value={rSched} onChange={(e) => setRSched(e.target.value)} /></div>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            <Icon name="clock" size={12} /> {rSched ? "Le job restera en file jusqu'à l'heure prévue (lancement manuel ou planificateur)." : rQty > 1 ? `${rQty} jobs seront mis en file.` : "Un job sera mis en file, prêt à être lancé."}
          </div>
        </Modal>
      )}
    </div>
  );
}
